import xss from 'xss'
import { AsyncFn, Log, minifyUrl } from '../../../common'
import { ForCompany, ForCompanyUser } from '../../Company/Company'
import { Email, WhiteLabelEmailer } from '../../Email/EmailTypes'
import { InvoiceV2Reader, InvoiceV2SendInvoice } from '../../InvoicesV2/InvoicesV2'
import { buildWhiteLabelSMSMessage } from '../../Sms/MessageBuilding'
import { ISmsMessenger } from '../../Sms/SmsTypes'
import { IBillingProfileReader } from '../CompanyBillingProfileTypes'
import { InvoiceV2PdfGenerator, SendInvoiceRequest } from './InvoiceTypes'

export const generateInvoicePaymentLink = (
  accountAppFullUrl: string,
  invoiceReferenceNumber: string,
  contactGuid = '',
  amountDueUsd?: number,
) => {
  const contactGuidSuffix = contactGuid ? `/${contactGuid}` : ''
  const amountDueUsdSuffix = amountDueUsd ? `?amountDueUsd=${amountDueUsd}` : ''
  return `${accountAppFullUrl}/invoice/${invoiceReferenceNumber}${contactGuidSuffix}${amountDueUsdSuffix}`
}

export const generateMinifiedInvoicePaymentLink = async (...props: Parameters<typeof generateInvoicePaymentLink>) =>
  minifyUrl(generateInvoicePaymentLink(...props))

export type CreateSendInvoiceSmsRequest = ForCompanyUser<Omit<SendInvoiceRequest, 'deliveryMethod'>>
export type InvoiceSenderSms = AsyncFn<CreateSendInvoiceSmsRequest>
export const createSendInvoiceSms =
  (sms: ISmsMessenger): AsyncFn<CreateSendInvoiceSmsRequest> =>
  async request => {
    const { to, body, companyGuid } = request

    try {
      await sms({
        companyGuid,
        from: 'Breezy Number',
        to,
        message: buildWhiteLabelSMSMessage({
          message: xss(body),
        }),
      })
      Log.info(`Consumer SMS sent for Send Invoice ${request.invoiceGuid}.`)
      return
    } catch (error) {
      Log.error(`Consumer SMS failed for Send Invoice ${request.invoiceGuid}.`, error)
      throw error
    }
  }

export type InvoiceSender = AsyncFn<
  ForCompanyUser<
    SendInvoiceRequest & {
      companyName: string
    }
  >
>

export type CreateSendInvoiceEmailRequest = ForCompany<
  Omit<SendInvoiceRequest, 'deliveryMethod'> & {
    companyName: string
    withPdfAttachment?: boolean
  }
>
export type InvoiceSenderEmail = AsyncFn<CreateSendInvoiceEmailRequest>
export const createSendInvoiceEmail =
  (
    emailer: WhiteLabelEmailer,
    billingProfileReader: IBillingProfileReader,
    invoiceV2PdfGenerator: InvoiceV2PdfGenerator,
    invoiceV2Reader: InvoiceV2Reader,
  ): AsyncFn<CreateSendInvoiceEmailRequest> =>
  async request => {
    const { companyGuid, to, subject, body, companyName, withPdfAttachment = false } = request

    const billingProfile = await billingProfileReader({ companyGuid })
    const sanitizedSubject = subject ? xss(subject) : subject

    const baseEmail: ForCompany<Email> = {
      documentReference: { documentType: 'invoice', documentGuid: request.invoiceGuid },
      companyGuid,
      to,
      subject: sanitizedSubject ?? '',
      message: xss(body),
      templateId: 'basic-branded-email',
      messageMetadata: {
        companyLogoUrl: billingProfile.businessLogoURL,
        companyName,
        contactPhoneNumber: billingProfile.businessBillingContactPhoneNumber.phoneNumber,
        contactEmailAddress: billingProfile.businessBillingEmailAddress.emailAddress,
        websiteUrl: billingProfile.businessWebsiteURL,
        websiteDisplayName: billingProfile.businessWebsiteDisplayName,
      },
    }

    if (withPdfAttachment) {
      const invoice = await invoiceV2Reader({ invoiceGuid: request.invoiceGuid })
      const pdf = await invoiceV2PdfGenerator(invoice)
      baseEmail.attachments = [
        {
          filename: `invoice-${request.invoiceGuid}.pdf`,
          content: pdf.toString('base64'),
          type: 'application/pdf',
          disposition: 'attachment',
        },
      ]
    }

    try {
      const publishEmailResult = await emailer(baseEmail)

      Log.info(`Consumer Email sent for Send Invoice ${request.invoiceGuid}.`)
      return publishEmailResult
    } catch (error) {
      Log.error(`Consumer Email failed for Send Invoice ${request.invoiceGuid}.`, error)
      throw error
    }
  }

export const createInvoiceSender =
  (
    emailSender: InvoiceSenderEmail,
    smsSender: InvoiceSenderSms,
    invoiceV2SendInvoice: InvoiceV2SendInvoice,
  ): InvoiceSender =>
  async request => {
    const { deliveryMethod, companyGuid, invoiceGuid, userGuid, paymentLink } = request

    if (deliveryMethod === 'EMAIL') {
      await emailSender(request)
    } else if (deliveryMethod === 'SMS') {
      await smsSender(request)
    } else {
      throw new Error('Could not Send Invoice through an invalid delivery method')
    }

    await invoiceV2SendInvoice({
      invoiceGuid,
      companyGuid,
      userGuid,
      paymentLink,
      deliveryMethod,
    })
  }
